import { useId } from 'react'
import { CUSTOM_PATTERN } from './custom-design-shapes'

export interface DesignPalette {
    background: string;
    pattern: string;
    details: string;
}

export const DEFAULT_DESIGN_PALETTE: DesignPalette = {
    background: '#a8e10c', pattern: '#cdff46', details: '#000000',
}

export const PALETTE_FIELDS: {key: keyof DesignPalette; label: string}[] = [
    {key: 'background', label: 'Фон'},
    {key: 'pattern', label: 'Узор'},
    {key: 'details', label: 'Значок и квадрат'},
]

const PRESETS = [
    {label: 'Лайм', palette: DEFAULT_DESIGN_PALETTE},
    {label: 'Лиловый', palette: {background: '#6844b5', pattern: '#c9a8ff', details: '#201033'}},
    {label: 'Океан', palette: {background: '#125a91', pattern: '#56d8e8', details: '#072b46'}},
    {label: 'Монохром', palette: {background: '#272727', pattern: '#bdbdbd', details: '#ffffff'}},
]

// Each region has its own vector fill, so antialiased edges are rendered
// against the current palette instead of carrying pixels from the source image.
export const CustomDesignPreview = ({palette, title, large = false}: {palette: DesignPalette; title: string; large?: boolean}) => {
    const gradientId = useId()
    const highlight = '#' + [1, 3, 5].map((offset) => {
        const channel = parseInt(palette.pattern.slice(offset, offset + 2), 16)
        return Math.round(channel + (255 - channel) * 0.35).toString(16).padStart(2, '0')
    }).join('')

    return (
        <div className={`catalog-image-wrap catalog-custom-preview${large ? ' catalog-preview-image' : ''}`}>
            <svg viewBox="0 0 407 256" role="img" aria-label={`${title}. Фон: ${palette.background}, узор: ${palette.pattern}, детали: ${palette.details}`}>
                <defs>
                    <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1="170" x2="407" y2="0">
                        <stop offset="60%" stopColor={palette.pattern} />
                        <stop offset="100%" stopColor={highlight} />
                    </linearGradient>
                </defs>
                <path d="M0 0H407V256H0Z" fill={palette.background} />
                <path d={CUSTOM_PATTERN.d} transform={CUSTOM_PATTERN.transform} fill={`url(#${gradientId})`} />
                <rect x="161.5" y="85.5" width="84" height="85" rx="9" fill={palette.details} />
                <g fill="none" stroke={palette.details} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M359 40L358 34L365 39L365 33" />
                    <path d="M370 31Q374 37 370 43M374 28Q380 37 375 46" />
                </g>
            </svg>
        </div>
    )
}

export const DesignColorEditor = ({palette, onChange}: {palette: DesignPalette; onChange: (palette: DesignPalette) => void}) => (
    <div className="catalog-color-editor">
        <p className="catalog-color-editor-title">Цвета вашей визитки</p>
        <div className="catalog-color-presets" aria-label="Готовые сочетания цветов">
            {PRESETS.map(({label, palette: preset}) => (
                <button type="button" key={label} aria-pressed={PALETTE_FIELDS.every(({key}) => palette[key] === preset[key])} onClick={() => onChange({...preset})}>
                    <span style={{background: `linear-gradient(135deg, ${preset.background} 50%, ${preset.pattern} 50%)`}} />
                    {label}
                </button>
            ))}
        </div>
        {PALETTE_FIELDS.map(({key, label}) => (
            <label className="catalog-color-field" key={key}>
                <span>{label}</span>
                <code>{palette[key].toUpperCase()}</code>
                <input type="color" aria-label={label} value={palette[key]} onChange={(event) => onChange({...palette, [key]: event.target.value})} />
            </label>
        ))}
        <button type="button" className="catalog-color-reset" onClick={() => onChange({...DEFAULT_DESIGN_PALETTE})}>Сбросить цвета</button>
    </div>
)
